import { useSearchParams } from "react-router-dom";
import { Table } from "@telluscompany/design-system";

import { useLocalization, translateOperationalModule } from "@i18n";
import type { PaginationMeta } from "@shared/api";
import { Gs3TablePaginator } from "@shared/components";
import { EMPTY_PAGINATION_META } from "@shared/constants/pagination.constants";
import { useUpdateTableUserPreferences } from "@shared/hooks";
import { useUserContext } from "@shared/contexts";

import type {
  AdminLicenseInventoryFilters,
  ListAdminLicenseInventoryResponseItem,
} from "../../api/license-inventory";
import type { AdminLicenseInventoryFilterData } from "../../schemas";
import { AdminLicenseInventoryFilter } from "./AdminLicenseInventoryFilter";

interface AdminLicenseInventoryTabProps {
  items: ListAdminLicenseInventoryResponseItem[];
  isLoading?: boolean;
  onFilter: (filters: Partial<AdminLicenseInventoryFilters>) => void;
  onClearFilter: () => void;
  initialFilters?: Partial<AdminLicenseInventoryFilterData>;
  paginatedMeta?: PaginationMeta;
}

const formatDate = (iso: string): string => {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString();
};

const AdminLicenseInventoryTab = ({
  items,
  onFilter,
  onClearFilter,
  initialFilters,
  paginatedMeta,
}: AdminLicenseInventoryTabProps) => {
  const { translate } = useLocalization();
  const { user: me } = useUserContext();
  const updateTableUserPreferences = useUpdateTableUserPreferences(me?.userPreferences);

  return (
    <div>
      <Table<ListAdminLicenseInventoryResponseItem>
        userId={me?.id ?? ""}
        tableId="admin-license-inventory-table"
        usersPreferencesFromServer={me?.userPreferences}
        updateUsersPreferencesOnServer={updateTableUserPreferences}
        useSearchParamsHook={useSearchParams}
        enableSortByUrl
        TableFilter={() => (
          <AdminLicenseInventoryFilter
            onFilter={onFilter}
            onClear={onClearFilter}
            initialFilters={initialFilters}
          />
        )}
        TablePaginator={
          <Gs3TablePaginator
            meta={paginatedMeta ?? EMPTY_PAGINATION_META}
            useUrlState
            useSearchParamsHook={useSearchParams}
          />
        }
        draggable
        enableTableColumnsSelector
        filterButtonText={translate(
          "admin.licenseInventory.table.filterButtonText",
        )}
        handleClickRow={() => {}}
        tableColumns={[
          {
            key: "complexName",
            label: translate("admin.licenseInventory.table.column.complex"),
            minWidth: "150px",
            sortType: "text",
          },
          {
            key: "structureName",
            label: translate("admin.licenseInventory.table.column.structure"),
            minWidth: "150px",
            sortType: "text",
          },
          {
            key: "moduleName",
            label: translate("admin.licenseInventory.table.column.module"),
            minWidth: "120px",
            sortType: "text",
          },
          {
            key: "validity",
            label: translate("admin.licenseInventory.table.column.validity"),
            minWidth: "180px",
          },
          {
            key: "quantity",
            label: translate("admin.licenseInventory.table.column.quantity"),
            minWidth: "80px",
            sortType: "number",
          },
          {
            key: "usedQuantity",
            label: translate("admin.licenseInventory.table.column.used"),
            minWidth: "80px",
          },
          {
            key: "availableQuantity",
            label: translate("admin.licenseInventory.table.column.available"),
            minWidth: "80px",
          },
        ]}
        tableData={items.map(
          (item) =>
            ({
              ...item,
              moduleName: translateOperationalModule(item.moduleName, translate),
              validity: `${formatDate(item.startDate)} – ${formatDate(
                item.endDate,
              )}`,
            }) as { [key: string]: unknown; color?: string },
        )}
      />
    </div>
  );
};

export default AdminLicenseInventoryTab;
